/**
 * A random, per-install device identifier.
 *
 * Used only to break ties in last-write-wins conflict resolution when two
 * devices carry the same `updatedAt` (data model Section 6). It is not an
 * account, not a fingerprint, and never leaves the device in v0.1 — from v0.5
 * it appears only inside sealed record bodies, never in a graph path (S-07).
 */
import type { DeviceId } from '@domain/types'

const KEY = 'thana_device_id'

let cached: DeviceId | undefined

export function getDeviceId(): DeviceId {
  if (cached !== undefined) return cached

  const stored = localStorage.getItem(KEY)
  if (stored !== null && stored.length > 0) {
    cached = stored
    return cached
  }

  const bytes = new Uint8Array(8)
  crypto.getRandomValues(bytes)
  cached = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  localStorage.setItem(KEY, cached)
  return cached
}

/**
 * Forget the device id. Part of `wipeEverything`.
 *
 * Not an account and not a fingerprint, but it is a persistent identifier, and
 * a user who asked to delete everything did not ask to keep it.
 */
export function clearDeviceId(): void {
  cached = undefined
  localStorage.removeItem(KEY)
}

/** Test seam. Not used by application code. */
export function resetDeviceIdForTests(): void {
  cached = undefined
}
